import { COACHING_MANAGER_NOTE_MAX_LENGTH } from "@repo/app-types/entities/teammatePlaybookCoachingManagerInput";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea, TextareaVariant } from "@repo/ui/Inputs/textarea";
import { useEffect, useState, type FC } from "react";

/** Show the character counter once the note is at 80% of the limit. */
const NOTE_COUNTER_THRESHOLD = COACHING_MANAGER_NOTE_MAX_LENGTH * 0.8;

export interface ManagerNoteEditorProps {
  /** The saved note being edited, or empty for a new note. */
  initialNote?: string;
  placeholder: string;
  onCancel: () => void;
  onSave: (note: string) => void;
  isSaving?: boolean;
  /** Reports whether the draft differs from the initial note. */
  onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * Inline editor card for a manager's note on the current coaching week.
 * Owns its draft state, so mounting a fresh instance resets the draft.
 */
export const ManagerNoteEditor: FC<ManagerNoteEditorProps> = (props) => {
  const {
    initialNote = "",
    placeholder,
    onCancel,
    onSave,
    isSaving,
    onDirtyChange,
  } = props;

  const [draft, setDraft] = useState(initialNote);

  const isDirty = draft !== initialNote;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <div className="rounded-lg border border-border-strong bg-white p-3 shadow-xs">
      <label className="sr-only" htmlFor="coaching-manager-note-editor">
        Manager note for this week
      </label>
      <Textarea
        id="coaching-manager-note-editor"
        placeholder={placeholder}
        value={draft}
        onChange={(newValue) =>
          setDraft(newValue.slice(0, COACHING_MANAGER_NOTE_MAX_LENGTH))
        }
        maxLength={COACHING_MANAGER_NOTE_MAX_LENGTH}
        variant={TextareaVariant.MINIMAL}
        autoFocus
        textareaClassName="min-h-16"
      />

      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Alpharun uses this note to inform future coaching.
        </p>
        {draft.length >= NOTE_COUNTER_THRESHOLD ? (
          <p className="text-xs text-muted-foreground tabular-nums">
            {draft.length}/{COACHING_MANAGER_NOTE_MAX_LENGTH}
          </p>
        ) : null}

        <div className="ml-auto flex justify-end gap-2">
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.SMALL}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            size={Size.SMALL}
            isDisabled={!draft.trim()}
            isLoading={isSaving}
            onClick={() => onSave(draft.trim())}
          >
            Save note
          </Button>
        </div>
      </div>
    </div>
  );
};
